import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export type UserRole = 'care_recipient' | 'caregiver' | 'admin';

interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  username?: string;
  role: UserRole;
  businessId?: string;
  inviteCode?: string;
  phoneNumber?: string;
}

/**
 * Why there is no profile for a signed-in user, so the dashboard can say so instead
 * of waiting for ever: `missing` = no `users/{uid}` document, `unavailable` = it
 * could not be read (offline, or refused by the security rules).
 */
export type ProfileProblem = 'missing' | 'unavailable' | null;

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  profileProblem: ProfileProblem;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  userProfile: null,
  profileProblem: null,
  loading: true,
  signOut: async () => {}
});

export function useAuth() {
  return useContext(AuthContext);
}

const KNOWN_ROLES: UserRole[] = ['care_recipient', 'caregiver', 'admin'];

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profileProblem, setProfileProblem] = useState<ProfileProblem>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setProfileProblem(null);

      if (user) {
        // Fetch user profile from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserProfile({
              id: user.uid,
              email: data.email || user.email || '',
              displayName: data.name || data.displayName || data.display_name || '',
              username: data.username,
              role: KNOWN_ROLES.includes(data.role) ? data.role : 'caregiver',
              businessId: data.businessId || data.business_id,
              inviteCode: data.inviteCode || data.invite_code,
              phoneNumber: data.phoneNumber || data.phone_number
            });
          } else {
            setUserProfile(null);
            setProfileProblem('missing');
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setUserProfile(null);
          setProfileProblem('unavailable');
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signOut = async () => {
    await firebaseSignOut(auth);
    setUserProfile(null);
    setProfileProblem(null);
  };

  const value = {
    currentUser,
    userProfile,
    profileProblem,
    loading,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
